import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Receipt,
  CreditCard,
  Calendar,
  Wallet,
  AlertTriangle,
  ArrowUpCircle,
  Ban,
  RotateCcw,
  CheckCircle2
} from 'lucide-react';

const formatCurrency = (value) => `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;

const formatDateLabel = (dateStr, includeTime = false) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return includeTime ? date.toLocaleString('pt-BR') : date.toLocaleDateString('pt-BR');
};

const getInvoiceBadge = (status) => {
  if (status === 'paid') return 'bg-emerald-100 text-emerald-700';
  if (status === 'overdue') return 'bg-red-100 text-red-700';
  if (status === 'cancelled') return 'bg-zinc-200 text-zinc-700';
  return 'bg-amber-100 text-amber-700';
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelConfirmPhrase, setCancelConfirmPhrase] = useState('');
  const [cancelConfirmChecks, setCancelConfirmChecks] = useState({
    understandAccess: false,
    understandIrreversible: false,
  });
  const [requestingCancel, setRequestingCancel] = useState(false);
  const [requestingReactivation, setRequestingReactivation] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      const response = await fetch(`${API}/billing/me`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Falha ao carregar faturamento');
      }
      setBilling(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar suas faturas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleCancelRequest = async () => {
    setRequestingCancel(true);
    try {
      const response = await fetch(`${API}/billing/cancel-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: cancelReason })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível registrar o cancelamento');
      }

      toast.success(data.message || 'Pedido de cancelamento registrado');
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelConfirmPhrase('');
      setCancelConfirmChecks({ understandAccess: false, understandIrreversible: false });
      fetchBilling();
    } catch (error) {
      toast.error(error.message || 'Erro ao registrar cancelamento');
    } finally {
      setRequestingCancel(false);
    }
  };

  const handleReactivationRequest = async () => {
    setRequestingReactivation(true);
    try {
      const response = await fetch(`${API}/billing/reactivate-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: 'Solicitado pelo cliente na área de faturas.' }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível solicitar a reativação');
      }
      toast.success(data.message || 'Pedido de reativação enviado');
      fetchBilling();
    } catch (error) {
      toast.error(error.message || 'Erro ao solicitar reativação');
    } finally {
      setRequestingReactivation(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
          <div className="space-y-6 animate-pulse">
            <div className="h-10 w-64 bg-zinc-200 rounded-xl" />
            <div className="grid md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="h-32 rounded-2xl bg-zinc-200" />
              ))}
            </div>
            <div className="h-72 rounded-2xl bg-zinc-200" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const profile = billing?.profile || {};
  const accountSegment = profile.account_segment === 'business' ? 'business' : 'personal';
  const upcomingInvoice = billing?.upcoming_invoice;
  const overdueInvoice = billing?.overdue_invoice;
  const invoices = billing?.invoices || [];
  const isFreePlan = false;
  const isTrialing = profile.plan_status === 'trialing' || profile.billing_status === 'trialing';
  const cancellationConfirmed = Boolean(profile.stripe_cancel_at_period_end) || billing?.customer_notice?.type === 'cancellation_confirmed';
  const cancellationPhraseIsValid = cancelConfirmPhrase.trim().toUpperCase() === 'CANCELAR MEU PLANO';
  const canSubmitCancellation = cancelConfirmChecks.understandAccess && cancelConfirmChecks.understandIrreversible && cancellationPhraseIsValid;
  const effectiveDateLabel = billing?.cancellation_effective_at ? formatDateLabel(billing.cancellation_effective_at) : null;
  const trialCancelUntilLabel = billing?.trial_cancel_until_date ? formatDateLabel(billing.trial_cancel_until_date) : null;
  const trialFirstChargeLabel = billing?.first_charge_date ? formatDateLabel(billing.first_charge_date) : null;
  const shouldShowUpcomingCharge = Boolean(upcomingInvoice) && !cancellationConfirmed;

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center gap-3">
              <Receipt className="w-8 h-8 text-violet-600" />
              Faturas e Cobrança
            </h1>
            <p className="text-zinc-500 mt-1">
              Acompanhe o que já foi pago, o próximo vencimento e suas ações de plano.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/app/billing')}>
              <ArrowUpCircle className="w-4 h-4 mr-2" />
              Upgrade de plano
            </Button>
            {!isFreePlan && billing?.cancellation_requested_at && (
              <Button variant="outline" className="rounded-full text-sky-700 border-sky-200 hover:bg-sky-50" onClick={handleReactivationRequest} disabled={requestingReactivation}>
                <RotateCcw className="w-4 h-4 mr-2" />
                {requestingReactivation ? 'Enviando...' : 'Pedir reativação'}
              </Button>
            )}
          </div>
        </div>

        {billing?.customer_notice ? (
          <Card className="mb-6 border-sky-200 bg-sky-50">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-sky-700 mt-0.5" />
              <div>
                <p className="font-medium text-sky-900">{billing.customer_notice.title}</p>
                <p className="text-sm text-sky-800 mt-1">{billing.customer_notice.message}</p>
                <p className="text-xs text-sky-700 mt-2">Atualizado em {formatDateLabel(billing.customer_notice.created_at, true)}</p>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Plano atual</p>
                  <p className="text-xl font-bold text-zinc-900 capitalize">
                    {profile.plan === 'pro' ? 'Pro' : profile.plan === 'business' ? 'Premium' : 'Starter'}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {profile.billing_cycle === 'annual' ? 'Anual' : profile.billing_cycle === 'monthly' ? 'Mensal' : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Valor do ciclo</p>
                  <p className="text-xl font-bold text-zinc-900">{formatCurrency(profile.billing_amount)}</p>
                  <p className="text-xs text-zinc-500">
                    {isTrialing ? 'Cobrança programada ao final do teste' : `Total pago: ${formatCurrency(billing?.paid_amount_total)}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Próxima cobrança</p>
                  <p className="text-xl font-bold text-zinc-900">
                    {cancellationConfirmed ? '-' : isTrialing ? (trialFirstChargeLabel || '-') : formatDateLabel(profile.next_billing_date)}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {cancellationConfirmed ? 'Recorrência cancelada' : (billing?.status_label || 'Sem cobrança agendada')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${overdueInvoice ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${overdueInvoice ? 'text-red-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Situação</p>
                  <p className="text-base font-bold text-zinc-900">{billing?.status_label || 'Sem cobrança ativa'}</p>
                  <p className="text-xs text-zinc-500">
                    {overdueInvoice?.rule?.action || (billing?.cancellation_requested_at ? `Acesso até ${effectiveDateLabel}` : 'Tudo Certo')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="border-zinc-200 lg:col-span-2">
            <CardHeader className="border-b border-zinc-100">
              <CardTitle>Histórico de Faturas</CardTitle>
              <CardDescription>
                Veja o que já foi pago e o que está por vencer no seu plano.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {invoices.length > 0 ? (
                invoices.map((invoice) => (
                  <div key={invoice.invoice_id} className="rounded-xl border border-zinc-200 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-medium text-zinc-900">
                          {invoice.plan === 'business' ? 'Premium' : invoice.plan === 'pro' ? 'Pro' : 'Starter'} • {invoice.billing_cycle === 'annual' ? 'Anual' : invoice.billing_cycle === 'monthly' ? 'Mensal' : 'Teste'}
                        </p>
                        <p className="text-sm text-zinc-500">
                          {invoice.status === 'cancelled' ? 'Cobrança cancelada' : `Vencimento: ${formatDateLabel(invoice.due_date)}`}
                          {invoice.paid_at ? ` • Pago em ${formatDateLabel(invoice.paid_at, true)}` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={`${getInvoiceBadge(invoice.status)} border-0 mb-2`}>
                          {invoice.label}
                        </Badge>
                        <p className="text-lg font-semibold text-zinc-900">{formatCurrency(invoice.amount)}</p>
                      </div>
                    </div>
                    {invoice.status === 'overdue' && invoice.rule ? (
                      <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                        {invoice.days_overdue} dia(s) em atraso. Próxima ação da régua: {invoice.rule.action}
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center text-zinc-500">
                  Nenhuma fatura registrada até o momento.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-zinc-200">
            <CardHeader className="border-b border-zinc-100">
              <CardTitle>Ações do plano</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="rounded-xl border border-zinc-200 p-4">
                <p className="text-sm font-medium text-zinc-900">Upgrade</p>
                <p className="text-sm text-zinc-500 mt-1">
                  Compare planos e aumente a capacidade da conta quando quiser.
                </p>
                <Button className="w-full rounded-full mt-4" onClick={() => navigate('/app/billing')}>
                  Ver opções de upgrade
                </Button>
              </div>

              {!isFreePlan ? (
                <div className="rounded-xl border border-zinc-200 p-4">
                  <p className="text-sm font-medium text-zinc-900">
                    {billing?.cancellation_requested_at
                      ? cancellationConfirmed
                        ? 'Cancelamento confirmado'
                        : 'Cancelamento em andamento'
                      : isTrialing
                        ? 'Cancelar teste'
                        : 'Cancelar plano'}
                  </p>
                  <p className="text-sm text-zinc-500 mt-1">
                    {billing?.cancellation_requested_at
                      ? cancellationConfirmed
                        ? `A recorrência já foi cancelada. Seu acesso segue liberado até ${effectiveDateLabel}.`
                        : `Seu pedido foi recebido e o acesso segue liberado até ${effectiveDateLabel}.`
                      : isTrialing
                        ? 'Se cancelar antes do fim do teste, não haverá cobrança no cartão.'
                        : 'Envie o pedido por aqui e acompanhe o prazo de desligamento sem perder o acesso imediato.'}
                  </p>
                  <div className="mt-4 flex gap-3">
                    {!billing?.cancellation_requested_at ? (
                      <Button variant="outline" className="rounded-full w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelDialogOpen(true)}>
                        Pedir cancelamento
                      </Button>
                    ) : (
                      <Button variant="outline" className="rounded-full w-full text-sky-700 border-sky-200 hover:bg-sky-50" onClick={handleReactivationRequest} disabled={requestingReactivation}>
                        {requestingReactivation ? 'Enviando...' : 'Pedir reativação'}
                      </Button>
                    )}
                  </div>
                </div>
              ) : null}

              {shouldShowUpcomingCharge ? (
                <div className="rounded-xl bg-sky-50 p-4">
                  <p className="text-sm font-medium text-sky-800">{isTrialing ? 'Primeira cobrança após o teste' : 'Próxima fatura'}</p>
                  <p className="text-2xl font-bold text-sky-900 mt-1">{formatCurrency(upcomingInvoice.amount)}</p>
                  <p className="text-sm text-sky-700 mt-1">
                    {isTrialing ? `Cobrança programada para ${trialFirstChargeLabel || formatDateLabel(upcomingInvoice.due_date)}` : `Vence em ${formatDateLabel(upcomingInvoice.due_date)}`}
                  </p>
                </div>
              ) : null}

              {billing?.cancellation_requested_at ? (
                <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
                  Pedido de cancelamento enviado em {formatDateLabel(billing.cancellation_requested_at, true)}. O sistema seguirá ativo até {effectiveDateLabel}.
                </div>
              ) : (
                <div className="rounded-xl bg-zinc-50 p-4 text-sm text-zinc-600">
                  {isTrialing
                    ? `Seu teste está ativo. Se cancelar até ${trialCancelUntilLabel || '-'}, o cartão não será cobrado. A cobrança está programada para ${trialFirstChargeLabel || '-'}`
                    : 'Você pode subir de plano quando quiser e pedir cancelamento diretamente por aqui.'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pedir cancelamento do plano</DialogTitle>
              <DialogDescription>
                Seu acesso continua liberado até o fim do período já pago. Preencha os campos abaixo para confirmar de forma segura.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
                Ao confirmar, o cancelamento será processado pelo time e o acesso seguirá liberado até {effectiveDateLabel || 'o fim do período atual'}.
              </div>

              <div className="space-y-2">
                <Label htmlFor="cancel-reason">Motivo do cancelamento</Label>
                <Textarea
                  id="cancel-reason"
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                  placeholder="Conte para nós o motivo do cancelamento"
                  rows={4}
                />
              </div>

              <label className="flex items-start gap-3 rounded-xl border border-zinc-200 p-4 text-sm text-zinc-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={cancelConfirmChecks.understandAccess}
                  onChange={(event) => setCancelConfirmChecks((current) => ({ ...current, understandAccess: event.target.checked }))}
                />
                <span>Entendo que o sistema ficará liberado somente até o fim do período já contratado.</span>
              </label>

              <label className="flex items-start gap-3 rounded-xl border border-zinc-200 p-4 text-sm text-zinc-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={cancelConfirmChecks.understandIrreversible}
                  onChange={(event) => setCancelConfirmChecks((current) => ({ ...current, understandIrreversible: event.target.checked }))}
                />
                <span>Entendo que, após o prazo final, o acesso será bloqueado até eventual reativação aprovada.</span>
              </label>

              <div className="space-y-2">
                <Label htmlFor="cancel-confirmation">Digite a frase para confirmar</Label>
                <Input
                  id="cancel-confirmation"
                  value={cancelConfirmPhrase}
                  onChange={(event) => setCancelConfirmPhrase(event.target.value)}
                  placeholder="CANCELAR MEU PLANO"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Fechar
              </Button>
              <Button onClick={handleCancelRequest} disabled={requestingCancel || !canSubmitCancellation}>
                {requestingCancel ? 'Enviando...' : 'Confirmar pedido'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
